import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase';
import './SuperAdminDashboard.css';
import './SuperAdminMobileMenu.css';

function PrivacyPolicy() {
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const menuRef = useRef(null);
  const toggleRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        mobileMenuOpen &&
        menuRef.current &&
        toggleRef.current &&
        !menuRef.current.contains(event.target) &&
        !toggleRef.current.contains(event.target)
      ) {
        setMobileMenuOpen(false);
      }
    };

    if (mobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [mobileMenuOpen]);

  // Fetch existing content
  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('legal_documents')
        .select('*')
        .eq('document_type', 'privacy_policy')
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching privacy policy:', error);
        setMessage({ type: 'error', text: 'Failed to load content' });
      } else if (data) {
        setContent(data.content || '');
      }
    } catch (error) {
      console.error('Error:', error);
      setMessage({ type: 'error', text: 'Failed to load content' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!content.trim()) {
      setMessage({ type: 'error', text: 'Please enter content before saving' });
      return;
    }

    try {
      setSaving(true);
      setMessage({ type: '', text: '' });

      // Check if document exists
      const { data: existing } = await supabase
        .from('legal_documents')
        .select('id')
        .eq('document_type', 'privacy_policy')
        .single();

      if (existing) {
        // Update existing document
        const { error } = await supabase
          .from('legal_documents')
          .update({
            content: content.trim(),
            updated_at: new Date().toISOString()
          })
          .eq('id', existing.id);

        if (error) throw error;
        setMessage({ type: 'success', text: 'Privacy Policy / EULA updated successfully!' });
      } else {
        // Insert new document
        const { error } = await supabase
          .from('legal_documents')
          .insert({
            document_type: 'privacy_policy',
            content: content.trim()
          });

        if (error) throw error;
        setMessage({ type: 'success', text: 'Privacy Policy / EULA saved successfully!' });
      }
    } catch (error) {
      console.error('Error saving privacy policy:', error);
      setMessage({ type: 'error', text: 'Failed to save content. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await supabase.auth.signOut();
      }
      localStorage.removeItem('isSuperAdminAuthenticated');
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
      localStorage.removeItem('isSuperAdminAuthenticated');
      navigate('/');
    }
  };

  return (
    <div className="super-admin-dashboard">
      {/* Mobile Header */}
      <div className="mobile-header">
        <div className="mobile-header-left">
          <span className="mobile-header-text">Super Admin</span>
        </div>
        <button 
          ref={toggleRef}
          className="mobile-menu-toggle"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setMobileMenuOpen(!mobileMenuOpen);
          }}
          aria-label="Toggle menu"
          type="button"
        >
          <span className={`hamburger ${mobileMenuOpen ? 'open' : ''}`}>
            <span></span>
            <span></span>
            <span></span>
          </span>
        </button>
      </div>

      {/* Sidebar */}
      <div 
        ref={menuRef} 
        className={`sidebar ${mobileMenuOpen ? 'mobile-open' : ''}`}
      >
        <div className="sidebar-header">
          <img 
            src="https://gjamvfwvqlizginqdfhd.supabase.co/storage/v1/object/public/transport-uploads/WhatsApp%20Image%202025-11-21%20at%2011.57.28%20AM%20(1).jpeg" 
            alt="Nexy App Logo" 
            className="sidebar-logo"
          />
        </div>
        
        <nav className="sidebar-nav">
          <button
            className="nav-item"
            onClick={() => {
              navigate('/superadmin/dashboard');
              setMobileMenuOpen(false);
            }}
          >
            <span className="nav-label">Dashboard</span>
          </button>
          
          <button
            className="nav-item"
            onClick={() => {
              navigate('/superadmin/add-school');
              setMobileMenuOpen(false);
            }}
          >
            <span className="nav-label">Add School</span>
          </button>
          
          <button
            className="nav-item"
            onClick={() => {
              navigate('/superadmin/schools');
              setMobileMenuOpen(false);
            }}
          >
            <span className="nav-label">Schools</span>
          </button>

          <button
            className="nav-item"
            onClick={() => {
              navigate('/superadmin/terms-and-conditions');
              setMobileMenuOpen(false);
            }}
          >
            <span className="nav-label">Terms & Conditions</span>
          </button>

          <button
            className="nav-item active"
            onClick={() => {
              navigate('/superadmin/privacy-policy');
              setMobileMenuOpen(false);
            }}
          >
            <span className="nav-label">Privacy Policy / EULA</span>
          </button>
        </nav>
        
        <div className="sidebar-spacer"></div>
        
        <div className="sidebar-footer">
          <button 
            className="logout-btn" 
            onClick={() => {
              handleLogout();
              setMobileMenuOpen(false);
            }}
          >
            <span>Logout</span>
          </button>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="main-content">
        <div className="content-header">
          <h1>Privacy Policy / EULA</h1>
          <div className="user-info">
            <span>Super Admin</span>
            <div className="user-avatar" style={{ backgroundColor: '#FF8C42', background: '#FF8C42' }}>SA</div>
          </div>
        </div>
        
        <div className="content-body">
          <div className="dashboard-content">
            {message.text && (
              <div className={`message ${message.type}`} style={{ marginBottom: '1rem' }}>
                {message.text}
                <button onClick={() => setMessage({ type: '', text: '' })}>×</button>
              </div>
            )}

            <div style={{ 
              backgroundColor: '#FFFFFF', 
              padding: '2rem', 
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}>
              <div style={{ marginBottom: '1.5rem' }}>
                <label htmlFor="privacy-content" style={{ 
                  display: 'block', 
                  marginBottom: '0.5rem',
                  fontWeight: '600',
                  color: '#1F2937'
                }}>
                  Privacy Policy / EULA Content
                </label>
                <textarea
                  id="privacy-content"
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="Enter Privacy Policy / EULA content here..."
                  style={{
                    width: '100%',
                    minHeight: '500px',
                    padding: '1rem',
                    border: '1px solid #D1D5DB',
                    borderRadius: '6px',
                    fontSize: '1rem',
                    fontFamily: 'inherit',
                    resize: 'vertical',
                    lineHeight: '1.6'
                  }}
                  disabled={loading}
                />
              </div>

              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                <button
                  onClick={handleSave}
                  disabled={loading || saving}
                  className="super-admin-btn"
                  style={{
                    padding: '0.75rem 2rem',
                    fontSize: '1rem',
                    fontWeight: '600'
                  }}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PrivacyPolicy;
